import './App.scss';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck, faDownload, faPencil, faPlus, faShareAlt, faTimes, faUpload} from '@fortawesome/free-solid-svg-icons';
import {useEffect, useMemo, useState} from "react";
import Batch from "./components/Batch";
import Modal from "./components/Modal";

import {base_url, download, fetch_session_data, post} from "./service/index.js";
import {add_device, delete_device, edit_device} from "./service/device.js";
import {add_variable, delete_variable} from "./service/variable.js";
import {add_batch, add_keyframe, delete_batch, delete_keyframe, edit_keyframe} from "./service/batch.js";
import {faCopy, faTrashAlt} from "@fortawesome/free-regular-svg-icons";
import SensorIcon from "./img/sensor.svg";
import ActuatorIcon from "./img/actuator.svg";
import SensorActuatorIcon from "./img/sensor-actuator.svg";

const _DEFAULT_DEVICE = {};
const _DEFAULT_VARIABLE = {type: "generic"};
const _DEFAULT_BATCH = {};
const _DEFAULT_KEYFRAME = {type: "variable_change", delay: 0};

const W3CWebSocket = require('websocket').w3cwebsocket;

const useQuery = () => {
    let searchParams = {};
    new URLSearchParams(window.location.search).forEach((value, key) => {
        searchParams[key] = value;
    });
    return searchParams;
};

function App() {

    const query = useQuery();

    const [devices, setDevices] = useState([]);
    const [variables, setVariables] = useState([]);
    const [batch, setBatch] = useState([]);
    const [batchKeyFrames, setBatchKeyFrames] = useState([]);

    const [createDevice, setCreateDevice] = useState(_DEFAULT_DEVICE);
    const [createVariable, setCreateVariable] = useState(_DEFAULT_VARIABLE);
    const [createBatch, setCreateBatch] = useState(_DEFAULT_BATCH);
    const [createKeyframe, setCreateKeyframe] = useState(_DEFAULT_KEYFRAME);

    const [showAddDevice, setShowAddDevice] = useState(false);
    const [showAddVariable, setShowAddVariable] = useState(false);
    const [showAddBatch, setShowAddBatch] = useState(false);
    const [showAddKeyframe, setShowAddKeyframe] = useState(false);
    const [showShare, setShowShare] = useState(false);

    const [ws, setWs] = useState(new W3CWebSocket(process.env.REACT_APP_BASE_URL_WS + "?auth=" + localStorage.getItem("bearer"), 'echo-protocol'));

    const keyframes = useMemo(() => batchKeyFrames.map((keyframe, index) => {
        try {
            keyframe.value = JSON.parse(keyframe.value || "");
        } catch (e) {
        }
        return keyframe;
    }), [batchKeyFrames]);

    async function loadData() {
        let session_data = await fetch_session_data();
        console.log(session_data);
        setDevices(session_data.device);
        setVariables(session_data.variable);
        setBatch(session_data.batch);
        setBatchKeyFrames(session_data.batch_keyframe.map(k => ({...k, keyframe_data: k.keyframe_data ? JSON.parse(k.keyframe_data) : null})));
    }

    useEffect(() => {
        ws.onerror = function () {
            console.log('Connection Error');
        };

        ws.onopen = function () {
            console.log('WebSocket Client Connected');
        };

        ws.onclose = function () {
            console.log('echo-protocol Client Closed');
            setWs(new W3CWebSocket(process.env.REACT_APP_BASE_URL_WS + "?auth=" + localStorage.getItem("bearer"), 'echo-protocol'));
        };

        ws.onmessage = function (e) {
            if (typeof e.data === 'string') {
                let data = JSON.parse(e.data);
                console.log(data.data);
                switch (data.type) {
                    case "batch_run_update":
                        let batches = data.data.batches;
                        setBatch(prev => {
                            return prev.map(batch => {
                                let batch_update = batches[batch.uuid];
                                if (batch_update) {
                                    batch.status = batch_update.status;
                                    batch.progress = data.data.now - batch_update.started;
                                }
                                return batch;
                            });
                        });
                        setBatchKeyFrames(prev => {
                            let _new = Object.keys(batches).reduce((acc, batch_uuid) => {
                                let keyframes = batches[batch_uuid].keyframes.map(k => ({...k, batch: batch_uuid}));
                                return [...acc, ...keyframes];
                            }, []);
                            let _old = prev.filter(k => !_new.find(k2 => k2.batch === k.batch));
                            return [..._new, ..._old];
                        });
                        break;
                    case "variable_update":
                        setVariables(data.data);
                        break;
                    case "device_update":
                        setDevices(data.data);
                        break;
                    case "batch_keyframe_update":
                        setBatchKeyFrames(data.data);
                        break;
                    case "batch_update":
                        setBatch(data.data);
                        break;
                    case "reload_all":
                        loadData();
                        break;
                }
            }
        };

        return () => {
            ws.close();
        };
    }, [ws]);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (query.b && localStorage.getItem("bearer") !== `Bearer ${query.b}`) {
            if (localStorage.getItem("bearer")) {
                if (!confirm("Se continuar com este link partilhado, irá perder a sessão atual. Deseja continuar?")) {
                    window.history.replaceState(null, document.title, "/")
                    return;
                }
            }
            localStorage.setItem("bearer", `Bearer ${query.b}`);
            window.location.reload();
        }
    }, [query.b])

    /* DISPOSITIVOS */

    const handleCloseAddDevice = () => {
        setShowAddDevice(false);
        setCreateDevice({});
    }

    const handleAddDevice = async () => {
        try {
            await add_device({...createDevice, variables: createDevice.variables?.join(",")});
            handleCloseAddDevice();
        } catch (e) {
            alert(e.message || e);
        }
    }

    const handleEditDevice = async () => {
        try {
            await edit_device({...createDevice, variables: createDevice.variables?.join(",")});
            handleCloseAddDevice();
        } catch (e) {
            alert(e.message || e);
        }
    }

    const handleDeleteDevice = (uuid) => async () => {
        try {
            await delete_device(uuid);
        } catch (e) {
            alert(e.message || e);
        }
        setShowAddDevice(false);
    }

    const changeValueCreateDevice = (key, value) => {
        setCreateDevice(prev => ({...prev, [key]: value}));
    }

    /* VARIAVEIS */

    const handleCloseAddVariable = () => {
        setShowAddVariable(false);
        setCreateVariable(_DEFAULT_VARIABLE);
    }

    const handleAddVariable = async () => {
        try {
            await add_variable(createVariable.name, createVariable.type);
            handleCloseAddVariable();
        } catch (e) {
            alert(e.message || e);
        }
    }

    const handleDeleteVariable = (uuid) => async () => {
        try {
            await delete_variable(uuid);
        } catch (e) {
            alert(e.message || e);
        }
    }

    const changeValueCreateVariable = (key, value) => {
        setCreateVariable(prev => ({...prev, [key]: value}));
    }

    /* BATCH */
    const handleCloseAddBatch = () => {
        setShowAddBatch(false);
        setCreateBatch(_DEFAULT_BATCH);
    }

    const handleAddBatch = async () => {
        try {
            await add_batch(createBatch.designation);
            handleCloseAddBatch();
        } catch (e) {
            alert(e.message || e);
        }
    }

    const handleDeleteBatch = (uuid) => async () => {
        try {
            await delete_batch(uuid);
        } catch (e) {
            alert(e.message || e);
        }
    }

    const changeValueCreateBatch = (key, value) => {
        setCreateBatch(prev => ({...prev, [key]: value}));
    }

    /* KEYFRAME */
    const handleCloseAddKeyframe = () => {
        setShowAddKeyframe(false);
        setCreateKeyframe(_DEFAULT_KEYFRAME);
    }

    const handleOpenAddKeyframe = (batch_uuid, variable) => {
        setCreateKeyframe(prev => ({...prev, batch: batch_uuid, variable}));
        setShowAddKeyframe(true);
    }

    const handleOpenEditKeyframe = (keyframe) => {
        keyframe = {...keyframe};
        keyframe.value = JSON.stringify(keyframe.value);
        setCreateKeyframe(keyframe);
        setShowAddKeyframe(true);
    }

    const handleAddKeyframe = async () => {
        try {
            createKeyframe.value = JSON.parse(createKeyframe.value)
        } catch (e) {
        }
        try {
            await add_keyframe(createKeyframe.batch, createKeyframe);
            handleCloseAddKeyframe();
        } catch (e) {
            alert(e.message || e);
        }
    }
    const handleEditKeyframe = async () => {
        try {
            createKeyframe.value = JSON.parse(createKeyframe.value)
        } catch (e) {
        }
        try {
            await edit_keyframe(createKeyframe.batch, createKeyframe);
            handleCloseAddKeyframe();
        } catch (e) {
            alert(e.message || e);
        }
    }

    const changeValueCreateKeyframe = (key, value) => {
        setCreateKeyframe(prev => ({...prev, [key]: value}));
    }

    const handleDeleteKeyframe = (batch_id, keyframe_id) => async () => {
        try {
            await delete_keyframe(batch_id, keyframe_id);
            setShowAddKeyframe(false);
        } catch (e) {
            alert(e.message || e);
        }
    }

    /* SHARE */
    const handleCopy = async () => {
        await navigator.clipboard.writeText(`${process.env.REACT_APP_HOST}?b=${localStorage.getItem("bearer")?.split(" ")[1]}`);
    }

    const handleCloseShare = () => {
        setShowShare(false);
    }

    return (
        <div className="App">
            <div className={"left-col"}>
                <div className={"left-col-content"}>
                    <h3>
                        Variáveis
                        <a className={"button-round button-add"} onClick={() => setShowAddVariable(true)}>
                            <FontAwesomeIcon icon={faPlus}/>
                        </a>
                    </h3>
                    <div className={"grid gap-s"}>
                        {variables.map((variable, index) => {
                            return (
                                <div key={variable.uuid} className={"variable col-12"}>
                                    <span>#{variable.name}</span>
                                    <div onClick={handleDeleteVariable(variable.uuid)}>
                                        <FontAwesomeIcon icon={faTrashAlt}/>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    <h3>
                        Dispositivos
                        <div className={"button-round button-add"} onClick={() => setShowAddDevice(true)}>
                            <FontAwesomeIcon icon={faPlus}/>
                        </div>
                    </h3>
                    <div className={"grid gap-s"}>
                        {devices.map((device, index) => {
                            try {
                                device.actuator_state = JSON.parse(device.actuator_state);
                            } catch (e) {
                            }
                            return <div key={device.uuid} className={"device col-12"}>
                                <img className={"icon"} src={{
                                    sensor: SensorIcon,
                                    actuator: ActuatorIcon,
                                    "sensor-actuator": SensorActuatorIcon,
                                }[device.type]}/>
                                <div className={"description"}>
                                    <h4>{device.designation}</h4>
                                    <p>{{
                                        sensor: "Sensor",
                                        actuator: "Atuador",
                                        "sensor-actuator": "Sensor / Atuador",
                                    }[device.type]}{device.actuator_state && <> - <span className={"orange"}>&nbsp;{typeof device.actuator_state === 'object' ? JSON.stringify(device.actuator_state) : device.actuator_state}</span></>}</p>
                                    <p title={(device.variables?.split(",").map(v => `#${v}`) || []).concat(device.default_data?.split("/").map(v => `#${v.replace(":", " (")})`) || []).join(", ")}>
                                        {(device.variables?.split(",").map(v => `#${v}`) || []).concat(device.default_data?.split("/").map(v => `#${v.replace(":", " (")})`) || []).join(", ")}</p>
                                    {(device.type === "actuator") && <><p><span className={"elipsis"}>{device.callback_url}</span>
                                        <FontAwesomeIcon
                                            style={{cursor: "pointer"}}
                                            icon={faCopy}
                                            onClick={() => {
                                                navigator.clipboard.writeText(device.callback_url)
                                            }}/></p></>}
                                </div>
                                <div className={"button-round button-add button-dark"} onClick={() => {
                                    setCreateDevice({...device, variables: device.variables?.split(",")});
                                    setShowAddDevice("edit");
                                }}>
                                    <FontAwesomeIcon icon={faPencil}/>
                                </div>
                            </div>
                        })}
                    </div>
                </div>
                <div className={"buttons"}>
                    <a className={"button"} onClick={() => download('/session/backup')}><FontAwesomeIcon icon={faDownload}/> Exportar</a>
                    <label className={"button"} style={{marginTop: 0}}><FontAwesomeIcon icon={faUpload}/> Importar
                        <input type={"file"} style={{display: "none"}} onChange={e => {
                            const file = e.target.files[0];
                            if (!file) return;
                            let data = new FormData();
                            data.append('file', file);
                            post('/session/restore', data).then(() => {
                                window.location.reload();
                            });
                        }}/>
                    </label>
                    <label className={"button"} style={{marginTop: 0}} onClick={() => setShowShare(true)}>
                        <FontAwesomeIcon icon={faShareAlt}/> Partilhar
                    </label>
                </div>
            </div>
            <div className={"timelines"}>
                <div className={"card-body h-100"}>
                    <h3>
                        Simulações
                        <div className={"button-round button-add"} onClick={() => setShowAddBatch(true)}>
                            <FontAwesomeIcon icon={faPlus}/>
                        </div>
                    </h3>
                    <div className={"d-flex flex-wrap"}>
                        {batch.map((batch, index) => {
                            return <Batch key={index} batch={batch} keyFrames={keyframes}
                                          variables={variables}
                                          devices={devices}
                                          handleDeleteBatch={handleDeleteBatch}
                                          handleDeleteKeyframe={handleDeleteKeyframe}
                                          handleOpenAddKeyframe={handleOpenAddKeyframe}
                                          handleOpenEditKeyframe={handleOpenEditKeyframe}/>
                        })}
                    </div>
                </div>
            </div>
            <Modal open={showAddDevice} onClose={handleCloseAddDevice}>
                <h3 className={"titleModal"}>{createDevice.uuid ? "Editar" : "Adicionar"} dispositivo</h3>
                <div className={"modal-body"}>
                    <div className={"mb-3"}>
                        <label>
                            Designação / Nome
                            <input id={"designation"} type={"text"} required value={createDevice.designation} onChange={(e) => changeValueCreateDevice("designation", e.target.value)}/>
                        </label>
                    </div>
                    <div className={"mb-3"}>
                        <label>
                            Tipo de dispositivo
                            <select id={"type"} required value={createDevice.type || ""} onChange={(e) => changeValueCreateDevice("type", e.target.value)} className={"form-select"}>
                                <option value={""} disabled>- Selecione -</option>
                                <option value={"sensor"}>Sensor</option>
                                <option value={"actuator"}>Atuador</option>
                                <option value={"sensor-actuator"}>Sensor-Atuador</option>
                            </select>
                        </label>
                    </div>
                    {(createDevice.type === "sensor" || createDevice.type === "sensor-actuator") && <>
                        <div className={"mb-3"}>
                            <label>Variáveis a observar
                                <small>O sensor irá ser sensível a mudanças nestas variáveis</small>
                            </label>
                            {variables.map((variable) => {
                                return <label className={"checkbox"} key={variable.uuid}>
                                    <input type={"checkbox"} checked={createDevice.variables?.includes(variable.name) || false} onChange={(e) => {
                                        if (createDevice.variables?.includes(variable.name)) {
                                            changeValueCreateDevice("variables", createDevice.variables?.filter(v => v !== variable.name) || []);
                                        } else {
                                            changeValueCreateDevice("variables", [...(createDevice.variables || []), variable.name]);
                                        }
                                    }}/>
                                    #{variable.name}
                                </label>
                            })}
                        </div>
                        <div className={"mb-3"}>
                            <label>Valores adicionais
                                <small>Enviar estes valores fixos<br/><span style={{fontSize: "0.8em"}}>Formato: campo1:valor1/campo2:valor2</span></small>
                                <input type={"text"} required value={createDevice.default_data} onChange={(e) => changeValueCreateDevice("default_data", e.target.value)}/>
                            </label>
                        </div>
                        <div className={"mb-3"}>
                            <label>URL para notificar
                                <small>Sempre que forem detetadas mudanças, enviar valor das variáveis para o seguinte endereço</small>
                                <input type={"text"} required value={createDevice.post_url} onChange={(e) => changeValueCreateDevice("post_url", e.target.value)}/>
                            </label>
                        </div>
                    </>}
                    {createDevice.type === "actuator" && <>
                        <div className={"mb-3"}>
                            <label className={"callback_url"}>Endereço de Callback
                                <small>Chamar o seguinte endereço para notificar o atuador, enviar informações a avaliar no body</small>
                                <input type={"text"} readOnly value={createDevice.callback_url} placeholder={"Endereço será disponibilizado ao guardar"}/>
                                <FontAwesomeIcon icon={faCopy} onClick={() => {
                                    navigator.clipboard.writeText(createDevice.callback_url)
                                }}/>
                            </label>
                        </div>
                    </>}
                    {(createDevice.type === "actuator" || createDevice.type === "sensor-actuator") && <>
                        <div className={"mb-3"}>
                            <label>Expressão de Avaliação
                                <small>Aplicar a seguinte validação de valor ao receber notificação</small>
                                <input type={"text"} required value={createDevice.evaluate_actuation} onChange={(e) => changeValueCreateDevice("evaluate_actuation", e.target.value)}/>
                            </label>
                        </div>
                    </>}
                    {createDevice.uuid && <div className={"delete"} onClick={handleDeleteDevice(createDevice.uuid)}>
                        Apagar dispositivo
                    </div>}
                </div>
                <div className={"modal-footer"}>
                    <a className={"button btn-primary"} onClick={createDevice.uuid ? handleEditDevice : handleAddDevice}>
                        <FontAwesomeIcon icon={faCheck}/>
                        {createDevice.uuid ? "Guardar" : "Adicionar"}
                    </a>
                    <a className={"button-round"} onClick={handleCloseAddDevice}>
                        <FontAwesomeIcon icon={faTimes}/>
                    </a>
                </div>
            </Modal>
            <Modal open={showAddVariable} onClose={handleCloseAddVariable}>
                <h3 className={"titleModal"}>Adicionar variável</h3>
                <div className={"modal-body"}>
                    <div className={"mb-3"}>
                        <label>
                            Nome da Variável
                            <input type={"text"} id={"name"} required onChange={(e) => changeValueCreateVariable("name", e.target.value)}/>
                        </label>
                    </div>
                    <div className={"mb-3"}>
                        <label>
                            Tipo
                            <select id={"type"} defaultValue={"generic"} required disabled onChange={(e) => changeValueCreateVariable("type", e.target.value)} className={"form-select"}>
                                {/*<option value={""} disabled selected>- Selecione -</option>*/}
                                <option value={"generic"}>Genérico</option>
                                {/*<option value={"string"}>Texto</option>
                            <option value={"boolean"}>Booleano</option>*/}
                            </select>
                        </label>
                    </div>
                </div>
                <div className={"modal-footer"}>
                    <a className={"button btn-primary"} onClick={handleAddVariable}>
                        <FontAwesomeIcon icon={faCheck}/>
                        Adicionar
                    </a>
                    <a className={"button-round"} onClick={handleCloseAddVariable}>
                        <FontAwesomeIcon icon={faTimes}/>
                    </a>
                </div>
            </Modal>
            <Modal open={showAddBatch} onClose={handleCloseAddBatch}>
                <h3 className={"titleModal"}>Adicionar timeline</h3>
                <div className={"modal-body"}>
                    <div className={"mb-3"}>
                        <label>
                            Designação
                            <input type={"text"} id={"designation"} required onChange={(e) => changeValueCreateBatch("designation", e.target.value)}/>
                        </label>
                    </div>
                </div>
                <div className={"modal-footer"}>
                    <a className={"button btn-primary"} onClick={handleAddBatch}>
                        <FontAwesomeIcon icon={faCheck}/>
                        Adicionar
                    </a>
                    <a className={"button-round"} onClick={handleCloseAddBatch}>
                        <FontAwesomeIcon icon={faTimes}/>
                    </a>
                </div>
            </Modal>
            <Modal open={showAddKeyframe} onClose={handleCloseAddKeyframe}>
                <h3 className={"titleModal"}>{createKeyframe.id ? "Editar" : "Adicionar"} keyframe #{createKeyframe.variable}</h3>
                <div className={"modal-body"}>
                    {/*<div className={"mb-3"}>
                        <label>Tipo
                            <select id={"type"} required disabled value={createKeyframe.type} onChange={(e) => changeValueCreateKeyframe("type", e.target.value)} className={"form-select"}>
                                <option value={"variable_change"}>Mudança de Variável</option>
                            </select>
                        </label>
                    </div>*/}
                    <div className={"mb-3"}>
                        <label>Valor
                            <input id={"value"} required value={createKeyframe.value} onChange={(e) => changeValueCreateKeyframe("value", e.target.value)}/>
                        </label>
                    </div>
                    <div className={"mb-3"}>
                        <label>Atraso (ms)
                            <input type={"number"} id={"delay"} required value={createKeyframe.delay} onChange={(e) => changeValueCreateKeyframe("delay", e.target.value)}/>
                        </label>
                    </div>
                    {createKeyframe.id && <div className={"delete"} onClick={handleDeleteKeyframe(createKeyframe.batch, createKeyframe.id)}>
                        Apagar keyframe
                    </div>}
                </div>
                <div className={"modal-footer"}>
                    <a className={"button btn-primary"} onClick={createKeyframe.id ? handleEditKeyframe : handleAddKeyframe}>
                        <FontAwesomeIcon icon={faCheck}/>
                        {createKeyframe.id ? "Guardar" : "Adicionar"}
                    </a>
                    <a className={"button-round"} onClick={handleCloseAddKeyframe}>
                        <FontAwesomeIcon icon={faTimes}/>
                    </a>
                </div>
            </Modal>
            <Modal open={showShare} onClose={handleCloseShare}>
                <h3 className={"titleModal"}>Partilhar configuração</h3>
                <div className={"modal-body"}>
                    <div className={"mb-3 share-input"}>
                        <input autoFocus onClick={(e) => e.target.select()} onFocus={(e) => e.target.select()}
                               defaultValue={`${process.env.REACT_APP_HOST}?b=${localStorage.getItem("bearer")?.split(" ")[1]}`}/>
                        <a className={"button-round button-add"} onClick={handleCopy}>
                            <FontAwesomeIcon icon={faCopy}/>
                        </a>
                    </div>
                </div>
                <div className={"modal-footer"}>
                    <a className={"button-round"} onClick={handleCloseShare}>
                        <FontAwesomeIcon icon={faTimes}/>
                    </a>
                </div>
            </Modal>
        </div>);
}

export default App;
